// components/ScoreHelpModal.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import {
    HelpCircle,
    Info,
    BookOpen,
    X,
    ChevronLeft
} from "lucide-react";

export interface ScoreHelp {
    [key: string]: string;
}

interface ScoreHelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    scoreHelp: ScoreHelp;
    title?: string;
    projectName?: string;
    scoringKeywords: { keyword: string, score: number, thinking: string }[]
    likertScaleLabels: { label: string, score: number }[]
}

type Score = 1 | 2 | 3 | 4 | 5;

const scoreColors: Record<Score, string> = {
    1: "danger",
    2: "warning",
    3: "info",
    4: "primary",
    5: "success",
};

// Scores that have expandable data (keywords)
const EXPANDABLE_SCORES = ["1", "3", "5"];

export const ScoreHelpModal = ({
    isOpen,
    onClose,
    scoreHelp,
    title = "Score Interpretation Guide",
    projectName = "Evaluation Scoring",
    scoringKeywords,
    likertScaleLabels
}: ScoreHelpModalProps) => {
    const [expandedScore, setExpandedScore] = useState<string | null>(null);

    // Reset expanded state when modal closes
    useEffect(() => {
        if (isOpen) {
            setExpandedScore(null);
        }
    }, [isOpen]);

    // Memoized sorted scores
    const sortedScores = useMemo(() => {
        return Object.keys(scoreHelp).sort((a, b) => Number(b) - Number(a));
    }, [scoreHelp]);

    // Memoized expandable scores check
    const isExpandableScore = (score: string) =>
        EXPANDABLE_SCORES.includes(score);

    // Memoized keywords for expanded score
    const expandedKeywords = useMemo(
        () =>
            expandedScore
                ? scoringKeywords
                    .filter(item => item.score === Number(expandedScore))
                    .map(({ keyword, thinking }) => ({
                        keyword,
                        description: thinking,
                    }))
                : [],
        [expandedScore, scoringKeywords]
    );
    // Memoized handlers
    const handleScoreToggle = (score: string) => {
        if (isExpandableScore(score)) {
            setExpandedScore(prev => (prev === score ? null : score));
        }
    };

    const closeExpandedMode = () => setExpandedScore(null);

    const getScoreColor = (score: string) =>
        scoreColors[Number(score) as Score] ?? "secondary";

    const getScoreLabel = (score: string) =>
        likertScaleLabels.find(label => label.score === Number(score))?.label || "";

    // Memoized modal header
    const modalHeader = useMemo(() => (
        <Modal.Header closeButton className="border-bottom p-3">
            <div className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2 d-flex align-items-center justify-content-center w-40px h-40px">
                    <HelpCircle size={20} className="text-primary" />
                </div>
                <div>
                    <Modal.Title className="fw-bold">{title}</Modal.Title>
                    <p className="text-muted fs-8 mb-0">{projectName}</p>
                </div>
            </div>
        </Modal.Header>
    ), [title, projectName]);

    // Memoized expanded content
    const expandedContent = useMemo(() => {
        if (!expandedScore || !scoreHelp[expandedScore]) return null;

        return (
            <div className="animate__animated animate__fadeIn mb-4">
                <div className="p-4 bg-light rounded-3 border" style={{ borderColor: `var(--bs-${getScoreColor(expandedScore)})` }}>
                    <div className="d-flex align-items-start gap-3">
                        <div className="flex-grow-1">
                            {/* Close Expanded Mode Button */}
                            <div className="d-flex justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <BookOpen size={16} className="text-primary" />
                                    <h6 className="fw-bold mb-0 text-primary">Related Keywords</h6>
                                </div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={closeExpandedMode}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <ChevronLeft size={16} />
                                    Back
                                </Button>
                            </div>

                            {/* Keywords Table */}
                            <div className="mt-3 animate__animated animate__fadeIn">
                                <div className="table-responsive" style={{ maxHeight: "50vh", overflowY: "auto" }}>
                                    <table className="table table-sm table-bordered">
                                        <thead className={`bg-${getScoreColor(expandedScore)} bg-opacity-10`}>
                                            <tr>
                                                <th className="fw-semibold text-gray-700 p-2" style={{ width: "30%" }}>
                                                    Keyword
                                                </th>
                                                <th className="fw-semibold text-gray-700 p-2">
                                                    Description
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {expandedKeywords.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="p-2">
                                                        <span className="fw-semibold text-gray-800">
                                                            {item.keyword}
                                                        </span>
                                                    </td>
                                                    <td className="p-2">
                                                        <span className="text-gray-600 fs-7">
                                                            {item.description}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [expandedScore, scoreHelp, getScoreColor, expandedKeywords, closeExpandedMode]);

    // Memoized table rows
    const tableRows = useMemo(() => {
        return sortedScores.map((score) => {
            const isExpandable = isExpandableScore(score);

            return (
                <tr
                    key={score}
                    className={`
        score-help-row
        align-middle
        ${expandedScore === score ? 'is-expanded' : ''}
    `}
                >
                    <td className="p-3">
                        {isExpandable ? (
                            <Button
                                variant="link"
                                className="p-0 text-decoration-none d-flex align-items-center gap-3 w-100 text-start"
                                onClick={() => handleScoreToggle(score)}
                                aria-expanded={expandedScore === score}
                                aria-label={`Show keywords for score ${score}`}
                            >
                                <div
                                    className={`bg-${getScoreColor(score)} text-white d-flex align-items-center justify-content-center rounded-circle fw-bold`}
                                    style={{ width: 36, height: 36 }}
                                >
                                    {score}
                                </div>

                                <span className={`fw-semibold fs-6 text-${getScoreColor(score)}`}
                                >
                                    {getScoreLabel(score)}
                                </span>
                            </Button>
                        ) : (
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className={`bg-${getScoreColor(score)} text-white d-flex align-items-center justify-content-center rounded-circle fw-bold`}
                                    style={{ width: 36, height: 36 }}
                                >
                                    {score}
                                </div>

                                <span className={`fw-semibold fs-6 text-${getScoreColor(score)}`}
                                >
                                    {getScoreLabel(score)}
                                </span>
                            </div>
                        )}
                    </td>

                    <td className="p-3">
                        <span className="text-gray-600 fs-7">
                            {scoreHelp[score]}
                        </span>
                    </td>
                </tr>
            );
        });
    }, [
        sortedScores,
        isExpandableScore,
        expandedScore,
        getScoreColor,
        getScoreLabel,
        handleScoreToggle,
        scoreHelp
    ]);

    // Memoized footer
    const footer = useMemo(() => (
        <Modal.Footer className="border-top p-3">
            <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-1">
                    <HelpCircle size={14} className="text-muted" />
                    <span className="text-muted fs-8">
                        {expandedScore
                            ? 'Click "Back" to return'
                            : 'Click any score  (1, 3, 5) for Keywords view'
                        }
                    </span>
                </div>
            </div>
            <Button variant="secondary" size="sm" onClick={onClose}>
                Close
            </Button>
        </Modal.Footer>
    ), [expandedScore, onClose]);

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size="lg"
            centered
            backdrop="static"
            keyboard={true}
            className="score-help-modal"
        >
            {modalHeader}

            <Modal.Body className="p-3">
                {/* Expanded Score Details */}
                {expandedContent}

                {/* Score Comparison Table */}
                {!expandedScore && (
                    <div className="mt-3">
                        <div className="table-responsive">
                            <table className="table table-hover border rounded-3 overflow-hidden">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="fw-semibold text-gray-700 p-3" style={{ width: "32%" }}>
                                            <div className="d-flex align-items-center gap-2">
                                                Score
                                            </div>
                                        </th>
                                        <th className="fw-semibold text-gray-700 p-3">
                                            Description
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Modal.Body>

            {footer}
        </Modal>
    );
}

// Display name for debugging
ScoreHelpModal.displayName = 'ScoreHelpModal';

// Hook to manage modal state
export const useScoreHelpModal = () => {
    const [isOpen, setIsOpen] = useState(false);

    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);

    return {
        isOpen,
        openModal,
        closeModal,
    };
};